import { useParams, useNavigate } from 'react-router-dom'
import { useOrganizationDetail } from '@/features/organizations/hooks/useOrganizationDetail'
import { OrganizationDetailTabs } from '@/features/organizations/components/OrganizationDetailTabs'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Building2, ArrowLeft, Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'

export default function OrganizationDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()

  const { data: organization, isLoading, error } = useOrganizationDetail(orgId || '')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !organization) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Organization Not Found"
          description="The organization you're looking for doesn't exist"
          icon={<Building2 className="h-6 w-6" />}
        />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error ? 'Failed to load organization' : 'Organization not found'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/organizations')} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Organizations
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/organizations')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <PageHeader
            title={organization.displayName}
            description={`Organization details and management`}
            icon={<Building2 className="h-6 w-6" />}
          />
        </div>
      </div>

      {/* Organization Detail Tabs */}
      <OrganizationDetailTabs
        organization={organization}
      />
    </div>
  )
}

